import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { appId, db } from '@/lib/firebase';
import { getDonorDocIdForUser } from '@/lib/donor-doc-id';

interface UseWarriorNumberResult {
  warriorNumber: number | null;
  loading: boolean;
}

/**
 * Reads CF-11 `warriorNumber` from the donor rollup doc when the signed-in user owns it.
 */
export function useWarriorNumber(userId: string, logContext: string): UseWarriorNumberResult {
  const [warriorNumber, setWarriorNumber] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const donorDocId = getDonorDocIdForUser(userId);
    const donorRef = doc(db, 'artifacts', appId, 'donors', donorDocId);

    const unsubscribe = onSnapshot(
      donorRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setWarriorNumber(null);
          setLoading(false);
          return;
        }

        const value = snapshot.data()?.warriorNumber;
        setWarriorNumber(typeof value === 'number' && value > 0 ? value : null);
        setLoading(false);
      },
      (error) => {
        console.warn(
          `[${logContext}] Failed to load warrior number for donor "${donorDocId}":`,
          error
        );
        setWarriorNumber(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [logContext, userId]);

  return { warriorNumber, loading };
}
